import { useEffect, useState, type ReactNode } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  ChartLineUp,
  Gear,
  List,
  List as MenuIcon,
  MagnifyingGlass,
  Megaphone,
  Moon,
  Receipt,
  SignOut,
  Storefront,
  Sun,
  Tag,
  Truck,
  Users,
  Images,
  CaretLeft,
  X,
  type Icon,
} from "@phosphor-icons/react";
import type { Permission } from "@clubghor/auth";
import { cn, humanise } from "~/lib/utils";
import { useSession } from "~/lib/session";
import { useTheme } from "~/lib/theme";
import { Button, Kbd } from "~/components/ui/primitives";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Tooltip,
} from "~/components/ui/overlays";
import { CommandPalette } from "./CommandPalette";

interface NavItem {
  to: string;
  label: string;
  icon: Icon;
  permission: Permission;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

/**
 * Navigation is DATA, and each entry declares the permission it needs. A route
 * cannot appear in the sidebar without stating its permission, so the nav and the
 * server's authorization cannot disagree about who may see what.
 */
const NAV: NavSection[] = [
  {
    label: "Overview",
    items: [{ to: "/", label: "Dashboard", icon: ChartLineUp, permission: "dashboard.read" }],
  },
  {
    label: "Commerce",
    items: [
      { to: "/orders", label: "Orders", icon: Receipt, permission: "orders.read" },
      { to: "/products", label: "Products", icon: Storefront, permission: "products.read" },
      { to: "/categories", label: "Categories", icon: List, permission: "categories.read" },
      { to: "/coupons", label: "Coupons", icon: Tag, permission: "coupons.read" },
      { to: "/customers", label: "Customers", icon: Users, permission: "customers.read" },
      { to: "/delivery-areas", label: "Delivery areas", icon: Truck, permission: "delivery.read" },
    ],
  },
  {
    label: "Content",
    items: [
      { to: "/media", label: "Media library", icon: Images, permission: "media.write" },
      { to: "/settings", label: "Storefront settings", icon: Megaphone, permission: "settings.read" },
    ],
  },
  {
    label: "Administration",
    items: [
      { to: "/staff", label: "Staff accounts", icon: Users, permission: "users.manage" },
      { to: "/profile", label: "Your account", icon: Gear, permission: "dashboard.read" },
    ],
  },
];

export function AppShell({ children, title, actions }: { children: ReactNode; title: string; actions?: ReactNode }) {
  const { user, can, signOut } = useSession();
  const { theme, toggle } = useTheme();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem("clubghor.admin.sidebar") === "collapsed";
    } catch {
      return false;
    }
  });

  const pathname = useRouterState({ select: (state) => state.location.pathname });

  // Cmd/Ctrl-K toggles search from anywhere. It used to only ever open, so
  // pressing it with the palette already up did nothing.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setPaletteOpen((open) => !open);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  function toggleCollapsed() {
    setCollapsed((previous) => {
      const next = !previous;
      try {
        localStorage.setItem("clubghor.admin.sidebar", next ? "collapsed" : "expanded");
      } catch {
        // Ignore storage failures; the in-memory state still applies.
      }
      return next;
    });
  }

  const sections = NAV.map((section) => ({
    ...section,
    items: section.items.filter((item) => can(item.permission)),
  })).filter((section) => section.items.length > 0);

  return (
    <div className="flex min-h-[100dvh] flex-col">
      {/* Skip link: the first tab stop, so keyboard users bypass the sidebar. */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-[var(--admin-radius-field)] focus:bg-[var(--cg-house)] focus:px-3 focus:py-2 focus:text-sm focus:text-[var(--cg-house-foreground)]"
      >
        Skip to content
      </a>

      <div className="flex flex-1">
        {/* ============================================================ sidebar */}
        <aside
          className={cn(
            "glass-chrome sticky top-0 hidden h-[100dvh] shrink-0 flex-col border-r md:flex",
            "transition-[width] duration-200 ease-[cubic-bezier(0.2,0,0,1)]",
            collapsed ? "w-[4.25rem]" : "w-[var(--admin-sidebar-width)]",
          )}
        >
          <div className={cn("flex h-[var(--admin-chrome-height)] items-center border-b border-[var(--cg-line)] px-3", collapsed && "justify-center")}>
            <Link to="/" className="flex min-w-0 items-center gap-2.5" aria-label="CLUBGHOR admin home">
              <img src="/brand/logo-mark.svg" alt="" width={26} height={29} className="h-7 w-auto shrink-0" />
              {!collapsed && (
                <span className="truncate font-display text-[0.9375rem] font-semibold text-[var(--cg-house)] dark:text-[var(--color-paper-100)]">
                  CLUBGHOR
                </span>
              )}
            </Link>
          </div>

          <nav className="scrollbar-thin flex-1 overflow-y-auto px-2 py-3" aria-label="Sections">
            <NavSections sections={sections} collapsed={collapsed} pathname={pathname} />
          </nav>

          <div className="border-t border-[var(--cg-line)] p-2">
            <button
              type="button"
              onClick={toggleCollapsed}
              aria-expanded={!collapsed}
              className={cn(
                "flex w-full items-center gap-2 rounded-[var(--admin-radius-chip)] px-2 py-2",
                "text-[0.8125rem] text-[var(--cg-content-muted)]",
                "transition-colors duration-120 ease-[cubic-bezier(0.2,0,0,1)] hover:bg-[color-mix(in_oklab,var(--cg-content)_6%,transparent)] hover:text-[var(--cg-content)]",
                collapsed && "justify-center",
              )}
            >
              <CaretLeft className={cn("size-4 shrink-0 transition-transform duration-200", collapsed && "rotate-180")} />
              {/* One accessible name: the visible label already says it, so a second
                  sr-only string made this announce as "Collapse Collapse sidebar". */}
              <span className={cn(collapsed && "sr-only")}>{collapsed ? "Expand sidebar" : "Collapse sidebar"}</span>
            </button>
          </div>
        </aside>

        {/* ============================================================= main */}
        <div className="flex min-w-0 flex-1 flex-col">
          <header className="glass-chrome sticky top-0 z-30 flex h-[var(--admin-chrome-height)] items-center gap-3 border-b px-4 md:px-6">
            {/* Mobile: the sidebar is hidden, so the brand and a menu button anchor
                the topbar. Without the drawer there was NO navigation below md —
                an owner on a phone could only reach other screens via the palette. */}
            <DialogPrimitive.Root open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
              <DialogPrimitive.Trigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden" aria-label="Open navigation menu">
                  <MenuIcon className="size-4" />
                </Button>
              </DialogPrimitive.Trigger>

              <DialogPrimitive.Portal>
                <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-[oklch(0_0_0/0.45)] md:hidden" />
                <DialogPrimitive.Content
                  className="glass-overlay fixed inset-y-0 left-0 z-50 flex w-[17rem] max-w-[85vw] flex-col md:hidden"
                  aria-describedby={undefined}
                >
                  <DialogPrimitive.Title className="sr-only">Navigation</DialogPrimitive.Title>

                  <div className="flex h-[var(--admin-chrome-height)] items-center justify-between border-b border-[var(--cg-line)] px-3">
                    <Link
                      to="/"
                      onClick={() => setMobileNavOpen(false)}
                      className="flex items-center gap-2.5"
                      aria-label="CLUBGHOR admin home"
                    >
                      <img src="/brand/logo-mark.svg" alt="" width={26} height={29} className="h-7 w-auto" />
                      <span className="font-display text-[0.9375rem] font-semibold text-[var(--cg-house)] dark:text-[var(--color-paper-100)]">
                        CLUBGHOR
                      </span>
                    </Link>
                    <DialogPrimitive.Close asChild>
                      <Button variant="ghost" size="icon-sm" aria-label="Close navigation menu">
                        <X className="size-4" />
                      </Button>
                    </DialogPrimitive.Close>
                  </div>

                  <nav className="scrollbar-thin flex-1 overflow-y-auto px-2 py-3" aria-label="Sections">
                    {/* Closing on navigate is what makes this usable: without it the
                        drawer stays over the screen you just asked for. */}
                    <NavSections
                      sections={sections}
                      collapsed={false}
                      pathname={pathname}
                      onNavigate={() => setMobileNavOpen(false)}
                    />
                  </nav>
                </DialogPrimitive.Content>
              </DialogPrimitive.Portal>
            </DialogPrimitive.Root>

            <Link to="/" className="flex items-center gap-2 md:hidden" aria-label="CLUBGHOR admin home">
              <img src="/brand/logo-mark.svg" alt="" width={22} height={25} className="h-6 w-auto" />
            </Link>

            <h1 className="truncate text-[0.9375rem] font-semibold text-[var(--cg-content)]">{title}</h1>

            <button
              type="button"
              onClick={() => setPaletteOpen(true)}
              className={cn(
                "ml-auto hidden h-9 items-center gap-2 rounded-[var(--admin-radius-field)] px-3 sm:flex",
                "border border-[var(--cg-line)] bg-[color-mix(in_oklab,var(--cg-surface)_70%,transparent)]",
                "text-[0.8125rem] text-[var(--cg-content-muted)]",
                "transition-colors duration-120 ease-[cubic-bezier(0.2,0,0,1)] hover:border-[var(--cg-line-strong)] hover:text-[var(--cg-content)]",
              )}
            >
              <MagnifyingGlass className="size-4" />
              <span>Search</span>
              <Kbd>⌘K</Kbd>
            </button>
            <Button
              variant="ghost"
              size="icon"
              className="ml-auto sm:hidden"
              onClick={() => setPaletteOpen(true)}
              aria-label="Search"
            >
              <MagnifyingGlass className="size-4" />
            </Button>

            <div className="flex items-center gap-1">
              {actions}

              {/*
                Theme toggle. The icon crossfades rather than swapping instantly,
                and the button always keeps a text alternative for screen readers.
              */}
              <Tooltip label={theme === "dark" ? "Switch to light" : "Switch to dark"}>
                <Button variant="ghost" size="icon" onClick={toggle} aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}>
                  {theme === "dark" ? <Sun className="size-4" /> : <Moon className="size-4" />}
                </Button>
              </Tooltip>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="flex h-9 items-center gap-2 rounded-[var(--admin-radius-field)] pl-1 pr-2 transition-colors duration-120 hover:bg-[color-mix(in_oklab,var(--cg-content)_6%,transparent)]"
                    aria-label="Your account"
                  >
                    <span className="grid size-7 place-items-center rounded-full bg-[var(--cg-house)] text-[0.6875rem] font-bold text-[var(--cg-house-foreground)]">
                      {initials(user?.name ?? "")}
                    </span>
                    <span className="hidden text-[0.8125rem] font-medium text-[var(--cg-content)] lg:block">
                      {user?.name ?? "Account"}
                    </span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuLabel>
                    {user?.name}
                    <span className="mt-0.5 block font-normal normal-case tracking-normal text-[var(--cg-content-muted)]">
                      {user?.email} · {user?.roleLabel ?? humanise(user?.role)}
                    </span>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="flex w-full items-center gap-2">
                      <Gear className="size-4 shrink-0" />
                      Your account
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem destructive icon={<SignOut className="size-4" />} onSelect={() => void signOut()}>
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>

          <main id="main" className="min-w-0 flex-1 px-4 py-5 md:px-6 md:py-6">
            {children}
          </main>
        </div>
      </div>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
  );
}

/** Shared by the desktop sidebar and the mobile drawer, so the two cannot drift. */
function NavSections({
  sections,
  collapsed,
  pathname,
  onNavigate,
}: {
  sections: NavSection[];
  collapsed: boolean;
  pathname: string;
  onNavigate?: () => void;
}) {
  return (
    <>
      {sections.map((section) => (
        <div key={section.label} className="mb-4 last:mb-0">
          {!collapsed && (
            <p className="px-2 pb-1.5 text-[0.625rem] font-bold uppercase tracking-[0.14em] text-[var(--cg-content-subtle)]">
              {section.label}
            </p>
          )}
          <ul className="flex flex-col gap-0.5">
            {section.items.map((item) => (
              <li key={item.to}>
                <SidebarLink
                  item={item}
                  collapsed={collapsed}
                  active={isActive(pathname, item.to)}
                  onNavigate={onNavigate}
                />
              </li>
            ))}
          </ul>
        </div>
      ))}
    </>
  );
}

function SidebarLink({
  item,
  collapsed,
  active,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  active: boolean;
  onNavigate?: () => void;
}) {
  const IconComponent = item.icon;

  const link = (
    <Link
      to={item.to}
      onClick={onNavigate}
      aria-current={active ? "page" : undefined}
      className={cn(
        "group relative flex items-center gap-2.5 rounded-[var(--admin-radius-chip)] px-2 py-2",
        "text-[0.8125rem] font-medium",
        "transition-colors duration-120 ease-[cubic-bezier(0.2,0,0,1)]",
        active
          ? "bg-[color-mix(in_oklab,var(--cg-primary)_14%,transparent)] text-[var(--color-mango-900)] dark:text-[var(--color-mango-200)]"
          : "text-[var(--cg-content-muted)] hover:bg-[color-mix(in_oklab,var(--cg-content)_6%,transparent)] hover:text-[var(--cg-content)]",
        collapsed && "justify-center",
      )}
    >
      {/*
        Active state uses the FILL weight and inactive uses the outline weight, so
        the state is carried by the glyph itself, not by colour alone.
      */}
      <IconComponent className="size-[1.125rem] shrink-0" weight={active ? "fill" : "regular"} />
      {!collapsed && <span className="truncate">{item.label}</span>}
      {collapsed && <span className="sr-only">{item.label}</span>}
    </Link>
  );

  return collapsed ? <Tooltip label={item.label} side="right">{link}</Tooltip> : link;
}

/** "/orders" matches "/orders/123" but not "/ordersomething". */
function isActive(pathname: string, to: string): boolean {
  if (to === "/") return pathname === "/";
  return pathname === to || pathname.startsWith(`${to}/`);
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((part) => part[0]?.toUpperCase() ?? "").join("") || "?";
}
